import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import DefaultLayout from "/github/workspace/node_modules/@adobe/gatsby-theme-aio/src/components/MDXFilter/index.js";
export const _frontmatter = {};
const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};
const InlineAlert = makeShortcode("InlineAlert");
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "programming-languages"
    }}>{`Programming languages`}</h1>
    <p>{`As mentioned earlier, UXP is a modern JavaScript (ECMA2015 or ES6) based extensibility platform. `}</p>
    <p>{`To begin, a little bit of knowledge of web technologies, such as JavaScript, HTML, and CSS, is required. There are some great materials available on the web that can teach you the basics but here are some of our recommendations. Feel free to use other sources to read about these topics.  `}</p>
    <InlineAlert slots="text1, text2" mdxType="InlineAlert" />
    <p>{`Please remember that since JavaScript, HTML or CSS are the foundational web technologies, online tutorials often assume that you are about to use them in a browser or a server-side JS engine. `}</p>
    <p>{`UXP is `}<strong parentName="p">{`not`}</strong>{` a full-blown browser or server-side JS engine like NodeJS. Though it allows you to use the same syntax, it does not intend to behave like a browser and consequently will support only a subset of the web APIs. This means, not all the HTML Elements or CSS classes or JavaScript APIs will be available in the UXP world. `}</p>
    <h2 {...{
      "id": "system-requirements"
    }}>{`System requirements`}</h2>
    <p>{`Your system must meet these minimum requirements for `}<a parentName="p" {...{
        "href": "https://helpx.adobe.com/in/indesign/system-requirements.html"
      }}>{`InDesign`}</a>{` and `}<a parentName="p" {...{
        "href": "https://helpx.adobe.com/indesign/kb/system-requirements-indesign-server.html"
      }}>{`InDesign Server`}</a>{` applications.`}</p>
    <h2 {...{
      "id": "reading-material"
    }}>{`Reading material`}</h2>
    <p>{`Start by getting acquainted with the basics of JS, HTML, and CSS.`}</p>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://javascript.info/intro"
        }}>{`Introduction to JavaScript`}</a>{` and its `}<a parentName="li" {...{
          "href": "https://developer.mozilla.org/en-US/docs/Learn/Getting_started_with_the_web/JavaScript_basics"
        }}>{`basics`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://www.w3schools.com/js/js_versions.asp"
        }}>{`JavaScript versions`}</a>{` and `}<a parentName="li" {...{
          "href": "https://www.w3schools.com/js/js_es6.asp"
        }}>{`ECMA2015 (or ES6)`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://www.w3schools.com/html/html_intro.asp"
        }}>{`HTML Basics`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://www.w3schools.com/css/css_intro.asp"
        }}>{`CSS Basics`}</a>{`, `}<a parentName="li" {...{
          "href": "https://www.w3schools.com/css/css_syntax.asp"
        }}>{`syntax`}</a>{`, and `}<a parentName="li" {...{
          "href": "https://www.w3schools.com/css/css_selectors.asp"
        }}>{`selectors`}</a></li>
    </ul>
    <p>{`Apart from the basics, the following additional topics will also come in handy. You need not go through each of them right away, but do bookmark them for later as it will help you understand the code snippets much more easily. `}</p>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://eloquentjavascript.net/14_dom.html"
        }}>{`Document Object Model or HTML DOM`}</a>{` and the global `}<a parentName="li" {...{
          "href": "https://www.w3schools.com/jsref/obj_window.asp"
        }}>{`window`}</a>{` and `}<a parentName="li" {...{
          "href": "https://www.w3schools.com/jsref/prop_win_document.asp"
        }}>{`document`}</a>{` object`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://javascript.info/introduction-browser-events"
        }}>{`DOM Events`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://www.w3schools.com/css/css_howto.asp"
        }}>{`Adding CSS`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://css-tricks.com/snippets/css/a-guide-to-flexbox/"
        }}>{`CSS layout using flexbox`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://javascript.info/promise-basics"
        }}>{`Promise`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://javascript.info/async-await"
        }}>{`Async/await`}</a></li>
      <li parentName="ul">{`Different ways of declaring JS functions - `}<a parentName="li" {...{
          "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/function"
        }}>{`traditional style`}</a>{` vs using the `}<a parentName="li" {...{
          "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Functions/Arrow_functions"
        }}>{`fat arrow`}</a></li>
    </ul>
    <h2 {...{
      "id": "chrome-debug-tool-cdt"
    }}>{`Chrome debug tool (CDT)`}</h2>
    <p>{`One of the easiest ways to debug these technologies is by using the Chrome Debug Tool. We will be using the same within our developer tool called UDT, which stands for the UXP Developer Tool. A high-level understanding of CDT will be very useful for you later.`}</p>
    <p>{`Please familiarize yourself with the `}<a parentName="p" {...{
        "href": "https://developer.chrome.com/docs/devtools/overview/"
      }}>{`Chrome Debug tool`}</a>{`, especially the ways to `}<a parentName="p" {...{
        "href": "https://developer.chrome.com/docs/devtools/dom/"
      }}>{`access DOM`}</a>{`, `}<a parentName="p" {...{
        "href": "https://developer.chrome.com/docs/devtools/css/"
      }}>{`modify CSS`}</a>{` and `}<a parentName="p" {...{
        "href": "https://developer.chrome.com/docs/devtools/javascript/"
      }}>{`debug JS`}</a>{` by adding breakpoints.`}</p>
    <h2 {...{
      "id": "frameworks"
    }}>{`Frameworks`}</h2>
    <p>{`So far we have spoken about plain JS, HTML, and CSS. However, the industry offers various frameworks that act as an abstract layer on top of these web technologies. These frameworks aim to provide you with a quicker, and more efficient way of writing reusable code.`}</p>
    <p>{`React, Vue, and Svelte are some of the popular frameworks, that developers often find useful. To use such frameworks is optional and is your personal choice. But, since these are one layer above the plain web technologies, you would require additional tools to run such projects.`}</p>
    <ul>
      <li parentName="ul">
        <p parentName="li"><a parentName="p" {...{
            "href": "https://nodejs.org/en/"
          }}>{`Node.js`}</a>{`: a JavaScript runtime environment. It's often used as a backend server in web environments. Go to the `}<a parentName="p" {...{
            "href": "https://nodejs.org/en/download/"
          }}>{`Node.js download page`}</a>{`, download the installer for your platform, and run it. This will also install `}<inlineCode parentName="p">{`npm`}</inlineCode>{`.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li"><a parentName="p" {...{
            "href": "https://www.npmjs.com"
          }}>{`npm`}</a>{`: a "package manager" bundled with Node which helps manage your project's dependencies (i.e., other code and files that are needed to make your plugin work).`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li"><a parentName="p" {...{
            "href": "https://yarnpkg.com"
          }}>{`yarn`}</a>{`: a "better" package manager than npm for Node. To install yarn you'll need to have npm installed first (see above). After that, use this command:`}</p>
        <pre parentName="li"><code parentName="pre" {...{
            "className": "language-bash"
          }}>{`npm install yarn --global
`}</code></pre>
      </li>
    </ul>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      